<script lang="ts" setup>
import {
  computed,
  navigateTo,
  onMounted,
  ref,
  useCommonIdEventData,
  useRoute,
} from '#imports';
import { useService } from '@kaokei/use-vue-service';
import CommonFooter from '@core/components/CommonFooter.vue';
import HTNotification from '@core/components/HTNotification.vue';
import HTSelectZone from '@core/components/HTSelectZone.vue';
import UserAvatarWithDropdown from '@core/components/UserAvatarWithDropdown.vue';
import SearchUnit from '@core/pages/components/index/SearchUnit.vue';
import { UserService } from '@core/services/user.service';

const userService = useService(UserService);
const route = useRoute();
const isGroup = computed(() => route.path.includes('oj/group'));
const isDiscuss = computed(() => route.path.includes('discuss/category'));
const selectedGroupKey = ref<number[]>([]);
const eventData = useCommonIdEventData();

const handleToGroup = () => {
  if (userService.user.myGroup.length > 0) {
    const item = userService.user.myGroup[0];
    navigateTo({
      path: '/oj/group',
      query: {
        gid: item.gid,
      },
    });
  }
};

const computeEventDataGroup = (record: any) => {
  return {
    group_id: record.gid,
  };
};

onMounted(() => {
  if (isGroup.value) {
    selectedGroupKey.value.push(eventData.group_id as number);
  }
});
</script>

<template>
  <a-layout>
    <header
      class="sticky top-0 z-30 border-b border-b-lightblack bg-white/95 backdrop-blur-sm"
    >
      <div class="mx-auto flex h-hnav w-wcontent items-center gap-2 transition-all">
        <NuxtLink
          v-track:oj_tab_logo_click.click
          to="/"
          class="flex items-center py-1"
        >
          <img class="h-[30px]" src="@core/assets/image/logo@2x.png" alt="logo" />
        </NuxtLink>

        <HTSelectZone class="!ml-[-3px]" />

        <NuxtLink
          v-track:newoj_navi_homepage_show
          v-track:newoj_navi_homepage_click.click
          to="/"
          class="menu-link menu-link-text ml-3"
          >首页</NuxtLink
        >
        <NuxtLink
          v-track:newoj_navi_problem_show
          v-track:newoj_navi_problem_click.click
          to="/oj/problem"
          class="menu-link menu-link-text"
        >
          题目
        </NuxtLink>
        <NuxtLink
          v-track:newoj_navi_training_show
          v-track:newoj_navi_training_click.click
          to="/oj/training"
          class="menu-link menu-link-text"
        >
          题单
        </NuxtLink>
        <NuxtLink
          v-track:newoj_navi_contest_show
          v-track:newoj_navi_contest_click.click
          to="/oj/contest"
          class="menu-link menu-link-text"
        >
          比赛
        </NuxtLink>

        <NuxtLink
          v-track:newoj_navi_discuss_show
          v-track:newoj_navi_discuss_click.click
          to="/discuss/category/"
          class="menu-link menu-link-text"
          :class="isDiscuss ? 'router-link-active' : ''"
        >
          讨论
        </NuxtLink>

        <NuxtLink
          v-if="
            userService.user.myGroup && userService.user.myGroup.length === 1
          "
          v-track:newoj_navi_group_show
          v-track:newoj_navi_group_click.click="
            computeEventDataGroup(userService.user.myGroup[0])
          "
          class="menu-link menu-link-text"
          :class="{ 'router-link-active': isGroup }"
          @click="handleToGroup"
          >小组
        </NuxtLink>

        <a-dropdown
          v-else-if="
            userService.user.myGroup && userService.user.myGroup.length > 1
          "
          placement="bottom"
          class="flex items-center"
        >
          <NuxtLink
            v-track:newoj_navi_group_show
            class="menu-link menu-link-text"
            :class="{ 'router-link-active': isGroup }"
            >小组
          </NuxtLink>
          <template #overlay>
            <a-menu v-model:selected-keys="selectedGroupKey" selectable>
              <a-menu-item
                v-for="item in userService.user.myGroup"
                :key="item.gid"
              >
                <NuxtLink
                  v-track:newoj_navi_group_click.click="
                    computeEventDataGroup(item)
                  "
                  :to="{
                    path: '/oj/group',
                    query: {
                      gid: item.gid,
                    },
                  }"
                  class="flex items-center rounded text-left"
                >
                  {{ item.name }}
                </NuxtLink>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>

        <NuxtLink
          v-track:newoj_navi_record_show
          v-track:newoj_navi_record_click.click
          to="/oj/problem/submit/list"
          class="menu-link menu-link-text"
        >
          记录
        </NuxtLink>

        <SearchUnit />

        <a-divider
          type="vertical"
          class="!mx-3 !h-[26px] bg-colorFillSecondary"
        ></a-divider>

        <HTNotification />

        <UserAvatarWithDropdown class="ml-3" />
      </div>
    </header>

    <a-layout-content class="!min-h-hcontent">
      <slot></slot>
    </a-layout-content>

    <a-layout-footer class="mt-[70px] !bg-white !px-0 !py-[30px] text-center">
      <CommonFooter />
    </a-layout-footer>
  </a-layout>
</template>

<style scoped>
.menu-link {
  padding: 4px 8px;
  line-height: 24px;
  font-size: 16px;
  @apply relative text-font3;
}
.menu-link-text:hover {
  @apply bg-colorFillSecondary text-subtitle;
  border-radius: 4px;
}
.menu-link-text.router-link-active {
  color: #222;
  font-weight: bold;
}
.menu-link-text.router-link-active::after {
  content: ' ';
  width: 70%;
  height: 3px;
  border-radius: 3px;
  @apply absolute bottom-0 left-0 right-0 mx-auto bg-primary;
}
:deep(.ant-dropdown-menu-item-selected .router-link-active) {
  font-weight: bold;
}
</style>
